import { createClient } from '@supabase/supabase-js';
import AsyncStorage from '@react-native-async-storage/async-storage';

export const SUPABASE_URL =
  process.env.EXPO_PUBLIC_SUPABASE_URL || 'https://qkxpubetmozhvnbzocsw.supabase.co';
export const SUPABASE_ANON_KEY =
  process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFreHB1YmV0bW96aHZuYnpvY3N3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTYwMDk4MjksImV4cCI6MjA3MTU4NTgyOX0.zEURzqM2m1UiliR-amm_cc2j0_4AN8HTAV9rPNqvoYs';
const supabaseUrl = SUPABASE_URL;
const supabaseAnonKey = SUPABASE_ANON_KEY;

// Para web, usar localStorage directamente; para móvil, usar AsyncStorage
// AsyncStorage en web automáticamente usa localStorage como fallback, pero es mejor ser explícito
const isWeb = typeof window !== 'undefined' && typeof document !== 'undefined';

function sleepMs(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return String(init.method).toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request) return input.method.toUpperCase();
  return 'GET';
}

/** Cloudflare/Supabase origin caído: 521–525. El HTML de error no trae CORS y el browser lo reporta como CORS. */
function shouldRetryStatus(status: number): boolean {
  return (
    status === 429 ||
    status === 502 ||
    status === 503 ||
    status === 504 ||
    status === 521 ||
    status === 522 ||
    status === 523 ||
    status === 524 ||
    status === 525
  );
}

/** Lecturas: timeout corto + 1 reintento. */
const FETCH_GET_TIMEOUT_MS = 12_000;
/**
 * Escrituras / Storage (POST…): timeout largo.
 * Un comprobante en /cdash fácilmente supera 8s y antes abortaba con
 * «signal is aborted without reason».
 */
const FETCH_MUTATION_TIMEOUT_MS = 120_000;

function abortAfter(ms: number, parent?: AbortSignal | null): { signal: AbortSignal; cancel: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  const onParentAbort = () => controller.abort();
  if (parent) {
    if (parent.aborted) controller.abort();
    else parent.addEventListener('abort', onParentAbort, { once: true });
  }
  return {
    signal: controller.signal,
    cancel: () => {
      clearTimeout(timer);
      parent?.removeEventListener('abort', onParentAbort);
    },
  };
}

function isAbortError(err: unknown): boolean {
  if (err == null || typeof err !== 'object') return false;
  const name = String((err as { name?: string }).name ?? '');
  const msg = String((err as { message?: string }).message ?? '');
  return name === 'AbortError' || /aborted|AbortError/i.test(msg);
}

async function fetchWithRetry(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const method = requestMethod(input, init);
  const retry = method === 'GET' || method === 'HEAD';
  const attempts = retry ? 2 : 1;
  /** POST/PUT/PATCH (RPC + Storage) necesitan más tiempo que un GET. */
  const timeoutMs = retry ? FETCH_GET_TIMEOUT_MS : FETCH_MUTATION_TIMEOUT_MS;
  let lastErr: unknown;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    const parentSignal = init?.signal ?? null;
    const timed = abortAfter(timeoutMs, parentSignal);
    try {
      const res = await fetch(input, { ...init, signal: timed.signal });
      if (retry && attempt < attempts && shouldRetryStatus(res.status)) {
        await sleepMs(350 * attempt * attempt);
        continue;
      }
      return res;
    } catch (err) {
      lastErr = err;
      if (retry && attempt < attempts && !parentSignal?.aborted) {
        await sleepMs(350 * attempt * attempt);
        continue;
      }
      // Timeout nuestro (no cancelación del caller): mensaje claro en UI.
      if (isAbortError(err) && !parentSignal?.aborted) {
        throw new Error(
          retry
            ? 'La conexión tardó demasiado. Intenta de nuevo.'
            : 'La subida o el guardado tardó demasiado. Revisa tu conexión e intenta de nuevo.'
        );
      }
      throw err;
    } finally {
      timed.cancel();
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('Failed to fetch');
}

const storage = isWeb ? {
  getItem: (key: string) => {
    try {
      return Promise.resolve(window.localStorage.getItem(key));
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return Promise.resolve(null);
    }
  },
  setItem: (key: string, value: string) => {
    try {
      window.localStorage.setItem(key, value);
      return Promise.resolve();
    } catch (error) {
      console.error('Error writing to localStorage:', error);
      return Promise.resolve();
    }
  },
  removeItem: (key: string) => {
    try {
      window.localStorage.removeItem(key);
      return Promise.resolve();
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return Promise.resolve();
    }
  },
} : AsyncStorage;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: storage as any,
    autoRefreshToken: true,
    persistSession: true,
    /** En web, el enlace «Confirma tu correo» trae tokens en la URL; hay que leerlos para crear sesión. */
    detectSessionInUrl: isWeb,
  },
  global: {
    fetch: fetchWithRetry,
  },
});

// Tipos para las tablas
export interface Ciudad {
  id: number;
  nombre: string;
  cover_web?: string;
  cover_mobile?: string;
  fecha_creacion: string;
  latitud_centro?: number | null;
  longitud_centro?: number | null;
}

export interface Patrocinador {
  id: number;
  nombre: string;
  email: string;
  telefono?: string;
  representante?: string;
  logo_fondo_claro?: string;
  logo_fondo_oscuro?: string;
  fecha_creacion: string;
}

export interface Restaurante {
  id: number;
  nombre_oficial: string;
  nombre_mostrar: string;
  ventas_promedio?: number | null;
  breve_resena?: string;
  representante?: string;
  numero_mesas?: number;
  ciudad_id?: number;
  email?: string;
  telefono?: string;
  instagram?: string;
  sede_ubicacion_corta?: string;
  direccion_completa?: string;
  ubicacion_gps?: string;
  sede_horario?: string;
  sedes?: any[];
  propuestas?: string;
  ediciones?: string;
  premios_obtenidos?: string;
  fecha_creacion: string;
}

export interface Edicion {
  id: number;
  nombre: string;
  fecha_inicio: string;
  fecha_fin: string;
  fecha_anuncio?: string | null;
  descripcion?: string;
  estado: string;
  fecha_creacion: string;
}

export interface Voto {
  id: number;
  restaurante_id: number;
  edicion_id: number;
  usuario_id: string;
  puntuacion: number;
  comentario?: string;
  fecha_voto: string;
}

export interface Estadistica {
  id: number;
  edicion_id: number;
  restaurante_id: number;
  total_votos: number;
  promedio_puntuacion: number;
  fecha_actualizacion: string;
}
